import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/app-shell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { PostCard, type FeedPost } from "@/components/post-card";
import { fetchFeed } from "@/lib/posts";
import { Button } from "@/components/ui/button";
import { Users, MapPin, Loader2, Calendar, Flame, Film, Image as ImageIcon, Eye } from "lucide-react";
import { MediaStrip } from "@/components/media-strip";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";

export const Route = createFileRoute("/communities/$slug")({
  loader: async ({ params }) => {
    try {
      const { data } = await supabase
        .from("communities")
        .select("name, description, city, country")
        .eq("slug", params.slug)
        .maybeSingle();
      return { seo: data ?? null };
    } catch {
      return { seo: null };
    }
  },
  head: ({ params, loaderData }) => {
    const c = loaderData?.seo as
      | { name: string; description: string | null; city: string | null; country: string | null }
      | null
      | undefined;
    const place = [c?.city, c?.country].filter(Boolean).join(", ");
    const title = c ? `${c.name}${place ? ` · ${place}` : ""} — Xamseen` : `Community — Xamseen`;
    const description =
      c?.description?.slice(0, 155) ||
      (c
        ? `Join the ${c.name} community on Xamseen${place ? ` in ${place}` : ""} — local reports, discussions and solutions from neighbours.`
        : "Explore a local Xamseen community: reports, discussions and solutions shared by neighbours.");
    const url = `https://xamseen.com/communities/${params.slug}`;
    return {
      meta: [
        { title: title.slice(0, 60) },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: CommunityPage,
});


interface CommunityFull {
  id: string; name: string; slug: string; description: string | null;
  country: string; region: string | null; city: string | null;
  member_count: number; created_by: string; created_at: string;
}

function CommunityPage() {
  const { slug } = Route.useParams();
  const { user } = useAuth();
  const [community, setCommunity] = useState<CommunityFull | null>(null);
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [isMember, setIsMember] = useState(false);
  const [loading, setLoading] = useState(true);
  const [joinBusy, setJoinBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data: c } = await supabase
      .from("communities").select("*").eq("slug", slug).maybeSingle();
    if (!c) { setLoading(false); return; }
    setCommunity(c as CommunityFull);
    const [feed, mem] = await Promise.all([
      fetchFeed({ communityId: (c as any).id, userId: user?.id, sort: "latest" }),
      user ? supabase.from("community_members").select("user_id").eq("community_id", (c as any).id).eq("user_id", user.id).maybeSingle() : Promise.resolve({ data: null }),
    ]);
    setPosts(feed);
    setIsMember(!!mem.data);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug, user?.id]);

  const toggleJoin = async () => {
    if (!user || !community) { toast.error("Sign in to join"); return; }
    setJoinBusy(true);
    if (isMember) {
      await supabase.from("community_members").delete().eq("community_id", community.id).eq("user_id", user.id);
      setIsMember(false);
      setCommunity({ ...community, member_count: Math.max(community.member_count - 1, 0) });
    } else {
      await supabase.from("community_members").insert({ community_id: community.id, user_id: user.id });
      setIsMember(true);
      setCommunity({ ...community, member_count: community.member_count + 1 });
    }
    setJoinBusy(false);
  };

  if (loading) return <AppShell><div className="flex justify-center py-20"><Loader2 className="h-5 w-5 animate-spin" /></div></AppShell>;
  if (!community) return <AppShell><div className="py-20 text-center text-muted-foreground">Community not found.</div></AppShell>;

  return (
    <AppShell>
      <div className="mb-6 rounded-2xl border border-border bg-gradient-to-br from-primary/10 via-card to-card p-6 shadow-[var(--shadow-card)]">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="font-display text-3xl font-semibold tracking-tight">{community.name}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{[community.city, community.region, community.country].filter(Boolean).join(", ")}</span>
              <span className="flex items-center gap-1"><Users className="h-4 w-4" />{community.member_count} members</span>
              <span className="flex items-center gap-1"><Calendar className="h-4 w-4" />Created {formatDistanceToNow(new Date(community.created_at), { addSuffix: true })}</span>
            </div>
            {community.description && <p className="mt-3 max-w-2xl text-sm text-foreground/80">{community.description}</p>}
          </div>
          <Button onClick={toggleJoin} disabled={joinBusy} variant={isMember ? "outline" : "default"}>
            {joinBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isMember ? "Leave" : "Join community"}
          </Button>
        </div>
      </div>

      <MediaStrip
        title="Community Highlights"
        icon={<Flame className="h-4 w-4 text-primary" />}
        communityId={community.id}
        sort="trending"
        windowDays={30}
        emptyHint="No standout posts in the last 30 days yet."
      />
      <MediaStrip
        title="Popular Photos"
        icon={<ImageIcon className="h-4 w-4 text-primary" />}
        communityId={community.id}
        filter="photo"
        sort="trending"
        windowDays={60}
      />
      <MediaStrip
        title="Trending Videos"
        icon={<Film className="h-4 w-4 text-primary" />}
        communityId={community.id}
        filter="video"
        sort="trending"
        windowDays={60}
      />
      <MediaStrip
        title="Most Viewed"
        icon={<Eye className="h-4 w-4 text-primary" />}
        communityId={community.id}
        sort="views"
        windowDays={90}
      />

      <h2 className="mb-3 font-display text-lg font-semibold">Posts</h2>
      {posts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-12 text-center">
          <p className="font-display font-medium">No posts in this community yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {user ? <>Share an update from <span className="font-medium">{community.name}</span>.</> : <Link to="/auth" className="text-primary underline">Sign in</Link>}
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {posts.map((p) => <PostCard key={p.id} post={p} onChanged={load} />)}
        </div>
      )}
    </AppShell>
  );
}
